import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceImageUploader } from '../../shared';
import { getStylePresets, getSupportedRatios, ReferenceImage } from '../../../services/imageGenerationApi';

interface LibraryItem {
  id: string;
  url: string;
  [key: string]: any;
}

interface ImageGenerationTabProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  imageCount: number;
  setImageCount: (count: 2 | 4) => void;
  selectedProvider: 'runway' | 'sdxl' | 'banana_nano' | 'vertex_gemini';
  selectedQuality: string;
  setSelectedQuality: (quality: string) => void;
  selectedAspectRatio: string;
  setSelectedAspectRatio: (ratio: string) => void;
  referenceImages: ReferenceImage[];
  addReferenceImage: (image: ReferenceImage) => void;
  addFromLibrary: (item: LibraryItem) => void;
  handleRemoveReferenceImage: (index: number) => void;
  canAddMore: boolean;
  maxImages: number;
  projectId?: string;
  isGenerating: boolean;
  handleGenerateImage: () => void;
  handleClose: () => void;
  // Advanced Runway controls
  showImageStyleAccordion: boolean;
  setShowImageStyleAccordion: (show: boolean) => void;
  showImageEnhancementAccordion: boolean;
  setShowImageEnhancementAccordion: (show: boolean) => void;
  selectedImageStyle: string;
  setSelectedImageStyle: (style: string) => void;
  selectedLighting: string;
  setSelectedLighting: (lighting: string) => void;
  selectedImageQuality: string;
  setSelectedImageQuality: (quality: string) => void;
  enhanceDetails: boolean;
  setEnhanceDetails: (enhance: boolean) => void;
  colorBoost: boolean;
  setColorBoost: (boost: boolean) => void;
  improveComposition: boolean;
  setImproveComposition: (improve: boolean) => void;
}

const ImageGenerationTab: React.FC<ImageGenerationTabProps> = ({
  prompt,
  setPrompt,
  imageCount,
  setImageCount,
  selectedProvider,
  selectedQuality,
  setSelectedQuality,
  selectedAspectRatio,
  setSelectedAspectRatio,
  referenceImages,
  addReferenceImage,
  addFromLibrary,
  handleRemoveReferenceImage,
  canAddMore,
  maxImages,
  projectId,
  isGenerating,
  handleGenerateImage,
  handleClose,
  showImageStyleAccordion,
  setShowImageStyleAccordion,
  showImageEnhancementAccordion,
  setShowImageEnhancementAccordion,
  selectedImageStyle,
  setSelectedImageStyle,
  selectedLighting,
  setSelectedLighting,
  selectedImageQuality,
  setSelectedImageQuality,
  enhanceDetails,
  setEnhanceDetails,
  colorBoost,
  setColorBoost,
  improveComposition,
  setImproveComposition
}) => {
  const { t } = useTranslation('aiGenerator');
  
  // Helper function to get quality presets with translations
  const getQualityPresets = () => [
    { name: t('image.qualityDraft'), value: 'draft', description: t('image.qualityDraftDesc') },
    { name: t('image.qualityStandard'), value: 'standard', description: t('image.qualityStandardDesc') },
    { name: t('image.qualityHigh'), value: 'high', description: t('image.qualityHighDesc') },
    { name: t('image.qualityUltra'), value: 'ultra', description: t('image.qualityUltraDesc') }
  ];
  
  // Helper function to get translated preset name
  const getTranslatedPresetName = (presetName: string): string => {
    const nameMap: Record<string, string> = {
      'Photorealistic': t('image.presetPhotorealistic'),
      'Cinematic': t('image.presetCinematic'),
      'Fashion Portrait': t('image.presetFashionPortrait'),
      'Product Shot': t('image.presetProductShot'),
      'Artistic': t('image.presetArtistic'),
      'Street Photography': t('image.presetStreetPhotography'),
      'Pixar 3D': t('image.presetPixar3D'),
      'Oil Painting': t('image.presetOilPainting'),
      'Digital Art': t('image.presetDigitalArt'),
      'Watercolor': t('image.presetWatercolor')
    };
    return nameMap[presetName] || presetName;
  };

  return (
    <>
      {/* Main Prompt Input */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('image.promptLabel')}
        </label>
        <textarea
          placeholder={t('image.promptPlaceholder')}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={{
            width: '100%',
            height: '100px',
            padding: '12px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit',
            outline: 'none'
          }}
        />
      </div>

      {/* Image Count Toggle */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('image.numberOfImages')}
        </label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setImageCount(2)}
            style={{
              padding: '10px 20px',
              backgroundColor: imageCount === 2 ? '#8b5cf6' : '#333',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            {t('image.twoImages')}
          </button>
          <button
            onClick={() => setImageCount(4)}
            style={{
              padding: '10px 20px',
              backgroundColor: imageCount === 4 ? '#8b5cf6' : '#333',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            {t('image.fourImages')}
          </button>
        </div>
        
        {/* Provider Logic Description */}
        <div style={{ 
          marginTop: '12px', 
          padding: '12px', 
          backgroundColor: 'rgba(139, 92, 246, 0.1)', 
          borderRadius: '8px',
          border: '1px solid rgba(139, 92, 246, 0.2)'
        }}>
          <div style={{ fontSize: '13px', fontWeight: '500', color: '#8b5cf6', marginBottom: '6px' }}>
            {t('image.generationStrategy')}
          </div>
          
          {imageCount === 4 && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
              {t('image.strategy4Images')}
            </div>
          )}
          
          {imageCount === 2 && referenceImages.length > 0 && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
              {t('image.strategy2ImagesWithRef')}
            </div>
          )}
          
          {imageCount === 2 && referenceImages.length === 0 && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#999' }}>
              {t('image.strategy2ImagesNoRef')}
            </div>
          )}
        </div>
      </div>

      {/* Reference Images (Runway Only) */}
      {selectedProvider === 'runway' && (
        <ReferenceImageUploader
          referenceImages={referenceImages}
          onAddReferenceImage={addReferenceImage}
          onAddFromLibrary={addFromLibrary}
          onRemoveReferenceImage={handleRemoveReferenceImage}
          canAddMore={canAddMore}
          maxImages={maxImages}
          projectId={projectId}
        />
      )}

      {/* Style Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
          {t('image.stylesLabel')}
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
          {getStylePresets(selectedProvider).map((preset, index) => (
            <button
              key={index}
              onClick={() => {
                const currentPrompt = prompt.trim();
                const newPrompt = currentPrompt ? `${currentPrompt}, ${preset.value}` : preset.value;
                setPrompt(newPrompt);
              }}
              style={{
                padding: '8px 12px',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '6px',
                color: '#8b5cf6',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontWeight: '500'
              }}
            >
              + {getTranslatedPresetName(preset.name)}
            </button>
          ))}
        </div>
      </div>

      {/* Image Options Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
        {/* Quality Dropdown - Only show for SDXL */}
        {selectedProvider === 'sdxl' && (
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              {t('image.quality')}
            </label>
            <select
              value={selectedQuality}
              onChange={(e) => setSelectedQuality(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '6px',
                backgroundColor: '#1a1a1a',
                color: 'white',
                fontSize: '14px',
                outline: 'none'
              }}
            >
              {getQualityPresets().map((preset, index) => (
                <option key={index} value={preset.value}>
                  {preset.name} - {preset.description}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Aspect Ratio */}
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
            {t('image.aspectRatio')}
          </label>
          <select
            value={selectedAspectRatio}
            onChange={(e) => setSelectedAspectRatio(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '6px',
              backgroundColor: '#1a1a1a',
              color: 'white',
              fontSize: '14px',
              outline: 'none'
            }}
          >
            {getSupportedRatios(selectedProvider).map((ratio, index) => (
              <option key={index} value={ratio.value}>
                {ratio.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ADVANCED RUNWAY IMAGE CONTROLS */}
      {selectedProvider === 'runway' && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ color: '#8b5cf6', margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600' }}>
            {t('image.advancedControls')}
          </h4>

          {/* Style & Quality Accordion */}
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={() => setShowImageStyleAccordion(!showImageStyleAccordion)}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: showImageStyleAccordion ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease'
              }}
            >
              <span>{t('image.styleQualitySettings')}</span>
              <span style={{ transform: showImageStyleAccordion ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                ▼
              </span>
            </button>
            
            {showImageStyleAccordion && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: 'rgba(139, 92, 246, 0.05)', 
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px'
              }}>
                {/* Style Presets */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    {t('image.stylePresets')}
                  </label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                    gap: '8px' 
                  }}>
                    {[
                      { key: 'photorealistic', label: '📸', name: t('image.stylePhoto'), desc: t('image.stylePhotoDesc') },
                      { key: 'cinematic', label: '🎬', name: t('image.styleCinema'), desc: t('image.styleCinemaDesc') },
                      { key: 'artistic', label: '🎨', name: t('image.styleArtistic'), desc: t('image.styleArtisticDesc') },
                      { key: 'commercial', label: '💼', name: t('image.styleCommercial'), desc: t('image.styleCommercialDesc') },
                      { key: 'portrait', label: '👤', name: t('image.stylePortrait'), desc: t('image.stylePortraitDesc') }
                    ].map(preset => (
                      <button
                        key={preset.key}
                        onClick={() => setSelectedImageStyle(selectedImageStyle === preset.key ? '' : preset.key)}
                        style={{
                          background: selectedImageStyle === preset.key ? '#8b5cf6' : '#333',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: '6px',
                          padding: '8px',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>{preset.label} {preset.name}</div>
                        <div style={{ fontSize: '9px', color: '#aaa' }}>{preset.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lighting Style */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    {t('image.lightingStyle')}
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'natural', label: t('image.lightingNatural') },
                      { value: 'dramatic', label: t('image.lightingDramatic') },
                      { value: 'soft', label: t('image.lightingSoft') },
                      { value: 'studio', label: t('image.lightingStudio') }
                    ].map(lighting => (
                      <button
                        key={lighting.value}
                        onClick={() => setSelectedLighting(selectedLighting === lighting.value ? '' : lighting.value)}
                        style={{
                          background: selectedLighting === lighting.value ? '#8b5cf6' : '#333',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          flex: '1',
                          minWidth: '80px'
                        }}
                      >
                        {lighting.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quality Preset */}
                <div style={{ marginBottom: '0' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    {t('image.qualityPreset')}
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[
                      { value: 'draft', label: t('image.qualityDraft') },
                      { value: 'standard', label: t('image.qualityStandard') },
                      { value: 'high', label: t('image.qualityHigh') },
                      { value: 'ultra', label: t('image.qualityUltra') }
                    ].map(quality => (
                      <button
                        key={quality.value}
                        onClick={() => setSelectedImageQuality(quality.value)}
                        style={{
                          background: selectedImageQuality === quality.value ? '#8b5cf6' : '#333',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          flex: '1',
                          minWidth: '80px'
                        }}
                      >
                        {quality.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Enhancement Options Accordion */}
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={() => setShowImageEnhancementAccordion(!showImageEnhancementAccordion)}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: showImageEnhancementAccordion ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease'
              }}
            >
              <span>{t('image.enhancementOptions')}</span>
              <span style={{ transform: showImageEnhancementAccordion ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                ▼
              </span>
            </button>
            
            {showImageEnhancementAccordion && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: 'rgba(139, 92, 246, 0.05)', 
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#ccc', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={enhanceDetails}
                      onChange={(e) => setEnhanceDetails(e.target.checked)}
                      style={{
                        width: '14px',
                        height: '14px',
                        accentColor: '#8b5cf6'
                      }}
                    />
                    {t('image.detailEnhancement')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#ccc', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={colorBoost}
                      onChange={(e) => setColorBoost(e.target.checked)}
                      style={{
                        width: '14px',
                        height: '14px',
                        accentColor: '#8b5cf6'
                      }}
                    />
                    {t('image.colorBoost')}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#ccc', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={improveComposition}
                      onChange={(e) => setImproveComposition(e.target.checked)}
                      style={{
                        width: '14px',
                        height: '14px',
                        accentColor: '#8b5cf6'
                      }}
                    />
                    {t('image.improveComposition')}
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleClose}
          style={{
            padding: '12px 24px',
            border: '1px solid rgba(255,255,255,0.2)',
            backgroundColor: 'transparent',
            color: 'white',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {t('common.cancel')}
        </button>
        
        <button
          onClick={handleGenerateImage}
          disabled={!prompt.trim() || isGenerating}
          style={{
            padding: '12px 24px',
            background: isGenerating ? '#666' : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: (isGenerating || !prompt.trim()) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {isGenerating 
            ? t('image.generating', { count: imageCount })
            : t('image.generate', { count: imageCount })
          }
        </button>
      </div>
    </>
  );
};

export default ImageGenerationTab;